import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { extractPhoneNumberDigits } from "@src/appV2/lib";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
// eslint-disable-next-line no-restricted-imports
import { getCognitoSingleton } from "@src/lib/cognito/src/lib/cognitoSingleton";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";

import { type ApiResponse, post } from "../../api/api";
import { IdentifyProviderEnum } from "./types";
import {
  backendMainotpSuccessResponseSchema,
  type OtpRequest,
  otpRequestSchema,
  otpSuccessResponseSchema,
  REQUEST_OTP_PATH,
  type RequestOtpErrorResponse,
  type RequestOtpSuccessResponse,
} from "./useRequestOtp";

export function useRequestAuthOtp(
  options: UseMutationOptions<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  > = {}
): UseMutationResult<
  ApiResponse<RequestOtpSuccessResponse>,
  AxiosError<RequestOtpErrorResponse>,
  OtpRequest
> {
  const isCognitoOnboardingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_ONBOARDING, {
    defaultValue: false,
  });
  const isCognitoPhoneLoginEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_COGNITO_PHONE_LOGIN, {
    defaultValue: false,
  });
  const isCognitoFailClosed = useCbhFlag(CbhFeatureFlag.COGNITO_FAIL_CLOSED, {
    defaultValue: false,
  });

  return useMutation<
    ApiResponse<RequestOtpSuccessResponse>,
    AxiosError<RequestOtpErrorResponse>,
    OtpRequest
  >({
    mutationFn: async ({ isSignup, phone, channel }) => {
      const phoneNumber = extractPhoneNumberDigits(phone);
      const phoneNumberInE164 = `+1${phoneNumber}`;

      // Cognito for signup (onboarding)
      if (isCognitoOnboardingEnabled && isSignup) {
        const cognitoAuth = getCognitoSingleton();

        try {
          await cognitoAuth.signUp(phoneNumberInE164);
          logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup,
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "otpIsValid",
              identityProvider: IdentifyProviderEnum.cognito,
            },
          } satisfies ApiResponse<RequestOtpSuccessResponse>;
        } catch (error) {
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error,
            metadata: {
              error,
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
              cognitoFailClosed: isCognitoFailClosed,
            },
          });
          if (isCognitoFailClosed) {
            throw error;
          }
        }
      }

      // Cognito for login (phone OTP)
      // Always try signUp first to force backend synchronization, then fall back to signIn.
      if (isCognitoPhoneLoginEnabled && !isSignup) {
        const cognitoAuth = getCognitoSingleton();

        // Step 1: Try signUp to force backend sync (creates Cognito user if not exists)
        try {
          await cognitoAuth.signUp(phoneNumberInE164);
          logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: true,
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "otpIsValid",
              identityProvider: IdentifyProviderEnum.cognito,
              isSignup: true,
            },
          } satisfies ApiResponse<RequestOtpSuccessResponse>;
        } catch (signUpError) {
          // signUp failed (user already exists), proceed to signIn
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error: signUpError,
            metadata: {
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
              fallbackToSignIn: true,
            },
          });
        }

        // Step 2: Fall back to signIn (requestPhoneOtp)
        try {
          await cognitoAuth.requestPhoneOtp(phoneNumberInE164);
          logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
            identityProvider: IdentifyProviderEnum.cognito,
            isSignup: false,
          });
          return {
            status: 200,
            statusText: "OK",
            data: {
              message: "otpIsValid",
              identityProvider: IdentifyProviderEnum.cognito,
              isSignup: false,
            },
          } satisfies ApiResponse<RequestOtpSuccessResponse>;
        } catch (error) {
          const cognitoError = error as { code?: string };
          logError(APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE, {
            error,
            metadata: {
              identityProvider: IdentifyProviderEnum.cognito,
              phoneNumber,
              cognitoFailClosed: isCognitoFailClosed,
              reason: cognitoError.code,
            },
          });
          if (isCognitoFailClosed) {
            throw error;
          }
          const recoverableCodes = ["auth/user-not-found", "auth/sms-otp-not-available"];
          if (!recoverableCodes.includes(cognitoError.code ?? "")) {
            throw error;
          }
        }
      }

      const response = await post({
        url: REQUEST_OTP_PATH,
        data: { isSignup, phone: phoneNumber, channel },
        requestSchema: otpRequestSchema,
        responseSchema: backendMainotpSuccessResponseSchema,
      });

      if (isSignup) {
        logEvent(APP_V2_USER_EVENTS.ONBOARDING_IDP_SELECTED, {
          identityProvider: IdentifyProviderEnum.firebase,
          isSignup,
        });
      }

      return {
        ...response,
        data: otpSuccessResponseSchema.parse({
          ...response.data,
          identityProvider: IdentifyProviderEnum.firebase,
        }),
      };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.REQUEST_OTP_FAILURE,
    },
    ...options,
  });
}
